import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import {
  EmailDetails,
  EmailSetting,
  Data,
  EmailSettingWithLoader,
  LoaderIntegrationStatsForDate,
  LoadsForEmailOnDate,
  EmailData,
  LoaderIntegrationLoad,
  LoaderIntegrationDataForLoad,
  PotentialMatch,
  Mine,
  PurchaseOrderData,
} from '~models/index';
import { filter, share, switchMap, take, map, tap } from 'rxjs/operators';
import { CrudService } from './crud.service';
import { environment } from '~environments/environment';
import { MineService } from './mine.service';
import { RouterStateService } from '~services/router-state.service';
import {
  LOADER_INTEGRATION_DATE,
  LOADER_INTEGRATION_LOADER_DATA_ID,
  LOADER_INTEGRATION_MINE_ID,
  LOADER_INTEGRATION_PURCHASE_ORDER_ID,
} from '../app-routing.constants';
import { HttpClient } from '@angular/common/http';

interface EmailSettingsBody {
  loaderId: string;
  email: string;
}

interface AnalyticsDataParameters {
  startDate: Date;
  endDate: Date;
  mineId: number;
}

export interface MineForFilter {
  id: number;
  name: string;
}

@Injectable({
  providedIn: 'root',
})
export class EmailSettingsV2Service {
  get route(): string {
    return environment.api + '/email_parser';
  }
  private emailSettings$$ = new BehaviorSubject<Data<EmailSettingWithLoader[]>>({
    meta: {
      loaded: false,
      loading: false,
    },
    data: [],
  });

  private emailData$$ = new BehaviorSubject<EmailData>(null);
  public emailData$ = this.emailData$$.asObservable();

  private purchaseOrderData$$ = new BehaviorSubject<PurchaseOrderData>(null);
  public purchaseOrderData$ = this.purchaseOrderData$$.asObservable();

  private loadsForDate$$ = new BehaviorSubject<LoaderIntegrationLoad[]>(null);
  public loadsForDate$ = this.loadsForDate$$.asObservable();

  private loadsForPurchaseOrder$$ = new BehaviorSubject<LoaderIntegrationLoad[]>(null);
  public loadsForPurchaseOrder$ = this.loadsForPurchaseOrder$$.asObservable();

  private loadDataForMatches$$ = new BehaviorSubject<LoaderIntegrationDataForLoad>(null);
  public loadDataForMatches$ = this.loadDataForMatches$$.asObservable();

  private potentialMatchesForLoad$$ = new BehaviorSubject<PotentialMatch[]>(null);
  public potentialMatchesForLoad$ = this.potentialMatchesForLoad$$.asObservable();

  private loaderAnalyticsData$$ = new BehaviorSubject<LoaderIntegrationStatsForDate[]>(null);
  public loaderAnalyticsData$ = this.loaderAnalyticsData$$.asObservable();

  private analyticsDataParameters$$ = new BehaviorSubject<AnalyticsDataParameters>(null);

  private minesForFilter$$ = new BehaviorSubject<MineForFilter[]>([]);
  public minesForFilter$ = this.minesForFilter$$.asObservable();

  public get emailSettings$(): Observable<Data<EmailSettingWithLoader[]>> {
    return this.emailSettings$$.asObservable().pipe(
      filter((settings) => !!settings),
      share(),
    );
  }

  constructor(
    private crud: CrudService,
    private mineService: MineService,
    private routerState: RouterStateService,
    private http: HttpClient,
  ) {
    this.loadingComplete = this.loadingComplete.bind(this);
    this.loadData();

    combineLatest([
      this.routerState.listenForParamChange$(LOADER_INTEGRATION_MINE_ID),
      this.routerState.listenForParamChange$(LOADER_INTEGRATION_DATE),
    ]).subscribe(([settingsID, date]) => {
      if (settingsID && date) {
        this.getLoadsOnDate(date, parseInt(settingsID, 10));
      } else {
        this.loadsForDate$$.next(null);
        this.emailData$$.next(null);
      }
    });

    this.routerState.listenForParamChange$(LOADER_INTEGRATION_LOADER_DATA_ID).subscribe((loaderDataID) => {
      if (loaderDataID) {
        this.getPotentialMatchesForLoad(parseInt(loaderDataID, 10));
      } else {
        this.potentialMatchesForLoad$$.next(null);
        this.loadDataForMatches$$.next(null);
      }
    });

    this.routerState.listenForParamChange$(LOADER_INTEGRATION_PURCHASE_ORDER_ID).subscribe((purchaseOrderID) => {
      if (purchaseOrderID) {
        this.getLoadsForPurchaseOrder(parseInt(purchaseOrderID, 10));
      } else {
        this.loadsForPurchaseOrder$$.next(null);
        this.purchaseOrderData$$.next(null);
      }
    });

    this.analyticsDataParameters$$.subscribe(async (parameters) => {
      if (parameters && parameters.startDate && parameters.endDate) {
        const analytics = await this.getEmailStatisticsV2(
          parameters.startDate,
          parameters.endDate,
          parameters.mineId,
        ).toPromise();
        this.loaderAnalyticsData$$.next(analytics);
      }
    });
  }

  public loadData() {
    this.startEmailSummariesLoading();
  }

  public isNewLoaderIntegration(): Observable<boolean> {
    return this.crud.httpClientReady.pipe(
      filter(Boolean),
      take(1),
      switchMap(() => this.crud.get(`${environment.api}/is_new_loader_integration `)),
    );
  }

  public getEmailsDetailsById(id: number): Observable<EmailDetails[]> {
    return this.crud.httpClientReady.pipe(
      filter(Boolean),
      take(1),
      switchMap(() => this.crud.get(`${this.route}/${id}`)),
    );
  }

  public saveEmail(body: EmailSettingsBody): Observable<any> {
    this.loadingOn();
    return this.crud.httpClientReady.pipe(
      filter(Boolean),
      take(1),
      switchMap(() => this.crud.post(this.route + '/v2/create_new_loader_integration', body)),
      tap(() => this.loadData()),
    );
  }

  public updateEmail(id: number, body: EmailSettingsBody): Observable<any> {
    this.loadingOn();
    return this.crud.httpClientReady.pipe(
      filter(Boolean),
      take(1),
      switchMap(() => this.crud.put(`${this.route}/${id}`, body)),
      tap(() => this.loadData()),
    );
  }

  public deleteEmailSettings(id: number): boolean {
    let success = false;
    this.loadingOn();
    this.crud.httpClientReady
      .pipe(
        filter(Boolean),
        take(1),
        switchMap(() => this.crud.delete(`${this.route}/${id}`)),
      )
      .subscribe(() => {
        this.loadData();
        success = true;
      });
    return true;
  }

  public downloadFile(fileName: string): Observable<any> {
    return this.crud.httpClientReady.pipe(
      filter(Boolean),
      take(1),
      switchMap(() => this.crud.get(this.route + '/link', { fileName })),
    );
  }

  private loadingOn() {
    const current = this.emailSettings$$.value;
    this.emailSettings$$.next({
      ...current,
      meta: {
        ...current.meta,
        loading: true,
      },
    });
  }

  private loadingComplete(data: EmailSettingWithLoader[], loaded = true) {
    const current = this.emailSettings$$.value;
    this.emailSettings$$.next({
      ...current,
      data,
      meta: {
        ...current.meta,
        loading: false,
        loaded: true,
      },
    });
  }

  private startEmailSummariesLoading() {
    this.loadingOn();
    this.crud.httpClientReady
      .pipe(
        filter(Boolean),
        take(1),
        switchMap(() =>
          combineLatest(
            this.mineService.mines$.pipe(filter((mines) => mines.meta.loaded)),
            this.crud.get(this.route + '/v2/list_loader_integration') as Observable<EmailSetting[]>,
          ),
        ),
        map(([mines, settings]) => {
          const mineMap: Record<number, string> = mines.data.reduce(
            (acc, mine) => ({
              ...acc,
              [mine.id]: mine.site.name,
            }),
            {},
          );
          return settings.map((setting) => ({
            ...setting,
            loader: {
              id: setting.loaderId,
              name: mineMap[setting.loaderId],
            },
          })) as EmailSetting[];
        }),
      )
      .subscribe(this.loadingComplete, (error) => {
        this.loadingComplete([], false);
      });
  }

  public setEmailStatisticsDateRange(startDate: Date, endDate: Date, mineId: number) {
    this.analyticsDataParameters$$.next({ startDate, endDate, mineId });
  }

  public getEmailStatisticsDateRange(): AnalyticsDataParameters {
    return this.analyticsDataParameters$$.value;
  }

  public getEmailStatisticsV2(
    startTime: Date,
    endTime: Date,
    mineId: number,
  ): Observable<LoaderIntegrationStatsForDate[]> {
    return this.crud.httpClientReady.pipe(
      filter(Boolean),
      take(1),
      switchMap(() =>
        this.crud.post(`${this.route}/v2/statistics_by_date`, {
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString(),
          mineId,
        }),
      ),
    );
  }

  private async getLoadsOnDate(date: string, mineId: number) {
    const value = await this.crud.httpClientReady
      .pipe(
        filter(Boolean),
        take(1),
        switchMap(() =>
          this.crud.post(`${this.route}/v2/loads_on_date`, {
            date,
            mineId,
          }),
        ),
      )
      .toPromise();

    this.loadsForDate$$.next(value.loads);
    this.emailData$$.next(value.emailData);
  }

  private async getLoadsForPurchaseOrder(purchaseOrderId: number) {
    const value = await this.crud.httpClientReady
      .pipe(
        filter(Boolean),
        take(1),
        switchMap(() =>
          this.crud.post(`${this.route}/v2/loads_for_purchase_order`, {
            purchaseOrderId,
          }),
        ),
      )
      .toPromise();

    this.loadsForPurchaseOrder$$.next(value.loads);
    this.purchaseOrderData$$.next(value.purchaseOrderData);
  }

  private async getPotentialMatchesForLoad(loaderIntegrationDataId: number) {
    const value = await this.crud.httpClientReady
      .pipe(
        filter(Boolean),
        take(1),
        switchMap(() => this.crud.get(`${this.route}/v2/potential_matches/${loaderIntegrationDataId}`)),
      )
      .toPromise();

    this.loadDataForMatches$$.next(value.loaderIntegrationData);
    this.potentialMatchesForLoad$$.next(value.matches);
  }

  public doManualFileUpload(file: File, loaderId: number) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mineId', loaderId.toString());

    console.log(formData);

    return this.http.post(`${this.route}/v2/manual_file_upload`, formData);
  }

  public setLoaderIntegrationDataMatch(potentialMatchId: number, orderId: number, loaderIntegrationDataId: number) {
    return this.crud.httpClientReady.pipe(
      filter(Boolean),
      take(1),
      switchMap(() =>
        this.crud.post(`${this.route}/v2/set_loader_integration_data_match`, {
          potentialMatchId,
          orderId,
          loaderIntegrationDataId,
        }),
      ),
    );
  }

  public unsetLoaderIntegrationDataMatch(potentialMatchId: number, orderId: number, loaderIntegrationDataId: number) {
    return this.crud.httpClientReady.pipe(
      filter(Boolean),
      take(1),
      switchMap(() =>
        this.crud.post(`${this.route}/v2/unset_loader_integration_data_match`, {
          potentialMatchId,
          orderId,
          loaderIntegrationDataId,
        }),
      ),
    );
  }

  public refreshPotentialMatchesForLoad() {
    if (this.loadDataForMatches$$.value) {
      this.getPotentialMatchesForLoad(this.loadDataForMatches$$.value.integrationDataId);
    }
  }

  public loadMinesForFilter() {
    this.crud.httpClientReady
      .pipe(
        filter(Boolean),
        take(1),
        switchMap(() => this.crud.get(`${this.route}/v2/loads_on_date/mines_for_filter`)),
      )
      .subscribe((mines) => {
        if (mines) {
          this.minesForFilter$$.next(mines);
        } else {
          this.minesForFilter$$.next([]);
        }
      });
  }
}
